import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import CustomTextInput from '@/components/form/CustomTextInput';
import CustomPhoneIntlTelInput from '@/components/form/CustomPhoneIntlTelInput';
import { DocumentType } from '@/core/enums/document-type';
import { OtpChannel } from '@/core/enums/otp-channel.enum';
import { UserSignupRequestOtpFormValues, userSignupRequestOtpSchema } from '../schemas/userSignupRequestOtpSchema';
import axios from 'axios';
import { UserSignupState, UserSignupAction } from '../states/userSignupReducer';
import { useApp } from '@/contexts/AppContext';
import { BasicResponse } from '@/core/models/common-types';
import ErrorMessage from '@/components/common/ErrorMessage';
import { extractErrorMessage } from '@/core/utils/utils';
import CustomCardRadioButton from '@/components/form/CustomCardRadioButton';
import CustomTurnstile from '@/components/form/CustomTurnstile';

interface UserSignupRequestOtpStepProps {
  id: string;
  onNext: (data: UserSignupRequestOtpFormValues) => void;
  state: UserSignupState;
  dispatch: React.Dispatch<UserSignupAction>;
}

const documentTypeOptions = [
  {
    id: DocumentType.NationalId,
    code: 'NATIONAL_ID',
    name: 'National ID',
  },
  {
    id: DocumentType.Passport,
    code: 'PASSPORT',
    name: 'Passport',
  },
];

const otpChannelOptions = [
  {
    id: OtpChannel.Sms,
    code: 'SMS',
    name: 'SMS',
  },
  {
    id: OtpChannel.Email,
    code: 'EMAIL',
    name: 'Email',
  },
];

const UserSignupRequestOtpStep: React.FC<UserSignupRequestOtpStepProps> = ({ onNext, id, state, dispatch }) => {
  const { baseUrl } = useApp();
  const [error, setError] = useState<BasicResponse | null>(null);
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<UserSignupRequestOtpFormValues>({
    resolver: zodResolver(userSignupRequestOtpSchema),
    defaultValues: {
      documentType: state.documentType,
      documentNo: state.documentNo ?? '',
      firstName: state.firstName,
      otpChannel: state.otpChannel,
      phoneNumber: state.phoneNumber,
      email: state.email,
      id: id,
    },
  });

  const otpChannel = useWatch({
    control,
    name: 'otpChannel',
  });

  useEffect(() => {
    console.log('Form Errors =>', errors);
  }, [errors]);

  const onSubmit = async (data: UserSignupRequestOtpFormValues) => {
    try {
      const { data: result } = await axios.post<{ sessionId: string }>(`${baseUrl}api/v2/UserSignup/RequestOtp`, data);
      dispatch({
        type: 'UPDATE',
        payload: {
          ...data,
          sessionId: result.sessionId,
        },
      });
      onNext(data);
    } catch (error) {
      setError(extractErrorMessage(error));
      setTurnstileResetKey((current) => current + 1);
    }
  };

  return (
    <div className="card">
      <div className="card-body">
        <ErrorMessage error={error} onClose={() => setError(null)} />
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="row">
            <div className="col-md-12">
              <CustomCardRadioButton
                control={control}
                name="documentType"
                label="Document Type"
                options={documentTypeOptions}
              />
            </div>
          </div>

          <div className="row">
            <div className="col-md-12">
              <CustomTextInput
                control={control}
                name="documentNo"
                label="Document Number"
                placeholder="Enter National ID or Passport number"
                required
              />
            </div>
          </div>

          <div className="row">
            <div className="col-md-12">
              <CustomTextInput
                control={control}
                name="firstName"
                label="First Name"
                placeholder="Enter First Name"
                required
              />
            </div>
          </div>

          <div className="row">
            <div className="col-md-12">
              <CustomCardRadioButton
                control={control}
                name="otpChannel"
                label="How would you like to receive your OTP?"
                options={otpChannelOptions}
              />
            </div>
          </div>

          {otpChannel === OtpChannel.Sms && (
            <div className="row">
              <div className="col-md-12">
                <CustomPhoneIntlTelInput control={control} name="phoneNumber" label="Phone Number" required />
              </div>
            </div>
          )}

          {otpChannel === OtpChannel.Email && (
            <div className="row">
              <div className="col-md-12">
                <CustomTextInput
                  control={control}
                  name="email"
                  label="Email Address"
                  placeholder="Enter Email Address"
                  required
                />
              </div>
            </div>
          )}
          <CustomTurnstile
            name="turnstileToken"
            control={control}
            action="request_otp_signup"
            resetKey={turnstileResetKey}
          />

          <div className="d-flex justify-content-end mt-3">
            <button type="submit" className="btn btn-primary">
              Request OTP
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserSignupRequestOtpStep;
